/* import __COLOCATED_TEMPLATE__ from './platforms-radio-group.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type MessengerSettingsService from 'embercom/services/messenger-settings-service';
import type MessengerSettingsTargetUserProvider from 'embercom/services/messenger-settings-target-user-provider';

type Platform = 'web' | 'ios' | 'android';

interface Args {
  disabled?: boolean;
  hideWebButton?: boolean;
}

interface Signature {
  Args: Args;
}

export default class PlatformsRadioGroup extends Component<Signature> {
  @service('messenger-settings-service') declare settingsService: MessengerSettingsService;
  @service declare messengerSettingsTargetUserProvider: MessengerSettingsTargetUserProvider;
  get selectedValue(): Platform {
    if (this.args.disabled) {
      return this.settingsService.preview.defaultPlatform;
    }
    return this.activeBrandMobilePlatform ?? this.settingsService.preview.selectedPlatform;
  }

  @action
  syncLockedPlatform() {
    let lockedPlatform = this.activeBrandMobilePlatform;
    if (lockedPlatform && this.settingsService.preview.selectedPlatform !== lockedPlatform) {
      this.setPreviewPlatform(lockedPlatform);
    }
  }

  get activeBrandMobilePlatform(): Platform | undefined {
    return this.messengerSettingsTargetUserProvider.settings?.lookAndFeel?.activeBrand
      ?.mobilePlatform;
  }

  get lockedPlatformIcon(): InterfaceIconName | null {
    let lockedPlatform = this.activeBrandMobilePlatform;
    if (!lockedPlatform) {
      return null;
    }
    let match = this.settingsService.preview.platforms.find(
      ({ value }) => value === lockedPlatform,
    );
    return (match?.icon as InterfaceIconName) ?? null;
  }

  get platformOptions(): { value: Platform; icon: string }[] {
    let platforms = this.settingsService.preview.platforms;
    if (this.args.hideWebButton) {
      platforms = platforms.filter(({ value }) => value !== 'web');
    }
    return platforms;
  }

  @action
  setPreviewPlatform(platform: Platform) {
    let { settings } = this.messengerSettingsTargetUserProvider;
    if (platform === 'web') {
      this.messengerSettingsTargetUserProvider.setUserTarget();
      if (settings?.lookAndFeel) {
        settings.lookAndFeel.activeBrand = settings.lookAndFeel.defaultBrand;
      }
    } else if (platform === 'ios' || platform === 'android') {
      this.messengerSettingsTargetUserProvider.setMobileTarget();
    } else {
      this.messengerSettingsTargetUserProvider.setUserTarget();
    }
    this.settingsService.changePlatform(platform);
    this.trackPlatformToggle(platform);
  }

  private trackPlatformToggle(platform: Platform) {
    this.settingsService.trackAnalyticsEvent({
      action: 'toggled',
      object: 'preview_platform',
      toggled_to: platform,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'MessengerSettings::Preview::PlatformsRadioGroup': typeof PlatformsRadioGroup;
    'messenger-settings/preview/platforms-radio-group': typeof PlatformsRadioGroup;
  }
}
